import type { ImgHTMLAttributes } from 'react';
import React from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

const DynamicWebWorker = dynamic(() => import('./DynamicWebWorkerImage'), {
  ssr: false,
});

/*
 * This component uses a web worker to fetch an image and
 * convert it to a blob url. This is useful for when you want to fetch an image
 * and display it in an img tag, but don't want to block the main thread.
 *
 * In all other cases, use NextImage, which is highly optimized and generates
 * multiple sizes of an image, which avoids downloading larger images than needed.
 *
 * WebWorkerImage uses a cache to avoid fetching the same image multiple times.
 * Cache is on client and uses src url as key to see if the image has been fetched
 * before.
 *
 * Use the `lazy` prop to only fetch the image when it is in view. This is useful
 * for when you have a list of images and you don't want to fetch all of them at
 * once.
 *
 * Unlike NextImage, WebWorkerImage always fetches the same size. Use width="100%"
 * Instead of setting the width explicitly, to avoid accidentally cutting off images
 * or skeletons
 *
 * WebWorkerImage downloads the new image if url changes.
 */
export const WebWorkerImage = React.memo(function WebWorkerImage({
  src,
  lazy = true,
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  lazy?: boolean;
}) {
  return typeof window !== 'undefined' ? (
    <DynamicWebWorker src={src} lazy={lazy} {...props} />
  ) : (
    <div {...props} className={clsx(props.className, 'loading after:rounded-sm')} />
  );
});
